import PropTypes from 'prop-types';
import { numberFormat } from '@/utils/unitUtils';

const findByLabel = (items, label) => items?.find(item => item.label === label);

const Kpi = ({ label, value, unit, tone = 'neutral' }) => {
  const valueColor = tone === 'positive' ? 'text-emerald-700' : tone === 'negative' ? 'text-rose-700' : 'text-gray-900';
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className={`text-base font-semibold tabular-nums ${valueColor}`}>{value}</span>
        {unit && <span className="text-xs text-gray-500">{unit}</span>}
      </div>
    </div>
  );
};

Kpi.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unit: PropTypes.string,
  tone: PropTypes.oneOf(['positive', 'negative', 'neutral']),
};

const formatRow = (row, digits = 2) => {
  if (!row || row.amount == null) return null;
  const scalar = typeof row.amount?.toNumber === 'function' ? row.amount.toNumber() : Number(row.amount);
  return { value: numberFormat(scalar, digits), unit: row.unit };
};

const HeadlineStrip = ({ levelizedCost, sellingPrice, lcohUnit, nodeLabel, facilityCharacteristics }) => {
  const plantCapacity = formatRow(findByLabel(facilityCharacteristics, 'Plant Capacity'), 2);
  const capacityFactor = formatRow(findByLabel(facilityCharacteristics, 'Capacity Factor'), 1);
  const averageOutput = formatRow(findByLabel(facilityCharacteristics, 'Asset Average Output'), 2);

  const hasSellingPrice = sellingPrice != null;
  const delta = hasSellingPrice && levelizedCost != null ? sellingPrice - levelizedCost : null;
  const deltaIsProfit = delta != null && delta > 0;

  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 py-4 border-b border-gray-200">
      <div className="flex items-end gap-10">
        <div className="flex flex-col gap-0.5">
          <div className="text-[11px] text-gray-500">
            Levelized cost{nodeLabel ? ` · ${nodeLabel}` : ''}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-semibold text-gray-900 tabular-nums">
              {levelizedCost != null ? numberFormat(levelizedCost, 2) : '—'}
            </span>
            {lcohUnit && <span className="text-base text-gray-500">{lcohUnit}</span>}
          </div>
        </div>
        {hasSellingPrice && (
          <div className="flex items-end gap-8 pb-1">
            <Kpi label="Selling price" value={numberFormat(sellingPrice, 2)} unit={lcohUnit} />
            {delta != null && (
              <Kpi
                label={deltaIsProfit ? 'Profit' : 'Loss'}
                value={`${deltaIsProfit ? '+' : ''}${numberFormat(delta, 2)}`}
                unit={lcohUnit}
                tone={deltaIsProfit ? 'positive' : 'negative'}
              />
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-x-10 gap-y-2">
        <Kpi label="Plant capacity" value={plantCapacity ? plantCapacity.value : '—'} unit={plantCapacity?.unit} />
        <Kpi label="Capacity factor" value={capacityFactor ? capacityFactor.value : '—'} unit={capacityFactor?.unit} />
        <Kpi label="Average output" value={averageOutput ? averageOutput.value : '—'} unit={averageOutput?.unit} />
      </div>
    </div>
  );
};

HeadlineStrip.propTypes = {
  levelizedCost: PropTypes.number,
  sellingPrice: PropTypes.number,
  lcohUnit: PropTypes.string,
  nodeLabel: PropTypes.string,
  facilityCharacteristics: PropTypes.array,
};

export default HeadlineStrip;
